import { Pencil } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { RoleListItem } from '@/api/admin-types'
import type { DualControlResult } from '@/api/types'
import { ErrorSurface, ProblemAlert } from '@/components/error-surface'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { MODULE_LABELS } from '@/features/approvals/request-types'
import type { PermissionCode } from '@/lib/permissions'

import { AdminDualControlNotice, AdminDualControlOutcome } from './dual-control'
import { useUpdateRolePermissions } from './mutations'
import { ADMIN_PERMISSIONS, useAdminPermissions } from './permissions'
import { useAdminPermissionCatalogue } from './queries'

/**
 * A role's permissions, grouped by module.
 *
 * `MODULE_LABELS` is `features/approvals/request-types.ts`'s, reused rather than duplicated: it is
 * already the application's one mapping from a permission's module prefix to the name an operator
 * reads, kept deliberately open — see its own comment — so a module the permission catalogue names
 * tomorrow renders under its own name today rather than needing this file to learn about it too.
 */
export function RolePermissionsPanel({ role }: { role: RoleListItem }) {
  const permissions = useAdminPermissions()
  const catalogue = useAdminPermissionCatalogue()
  const [editing, setEditing] = useState(false)

  const granted = useMemo(() => new Set<PermissionCode>(role.permissions), [role.permissions])

  const groups = (catalogue.data ?? [])
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter((permission) => granted.has(permission.code)),
    }))
    .filter((group) => group.permissions.length > 0)

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Granted permissions</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            {role.permissions.length} permission{role.permissions.length === 1 ? '' : 's'}, across{' '}
            {groups.length} module{groups.length === 1 ? '' : 's'}.
          </p>
        </div>
        {permissions.canManageRolePermissions ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            <Pencil />
            Edit permissions
          </Button>
        ) : null}
      </CardHeader>

      <CardContent>
        {catalogue.isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-64" />
          </div>
        ) : catalogue.isError ? (
          <ErrorSurface error={catalogue.error} onRetry={() => void catalogue.refetch()} />
        ) : role.permissions.length === 0 ? (
          <p className="text-sm text-muted">This role grants nothing yet.</p>
        ) : groups.length === 0 ? (
          // The catalogue answered but named none of this role's codes — a role holding a
          // permission the catalogue no longer lists, which is worth showing rather than hiding.
          <div className="space-y-1.5">
            <p className="text-sm text-muted">
              None of this role&apos;s permissions are in the current catalogue:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {role.permissions.map((code) => (
                <Badge key={code} tone="warning" className="font-mono">
                  {code}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.module}>
                <p className="text-xs font-semibold text-heading">
                  {MODULE_LABELS[group.module] ?? group.module}
                </p>
                <dl className="mt-1.5 space-y-1">
                  {group.permissions.map((permission) => (
                    <div key={permission.code} className="flex flex-wrap items-baseline gap-2">
                      <dt className="font-mono text-xs text-brand-text">{permission.code}</dt>
                      <dd className="text-xs text-body">{permission.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <EditRolePermissionsDialog role={role} open={editing} onOpenChange={setEditing} />
    </Card>
  )
}

function EditRolePermissionsDialog({
  role,
  open,
  onOpenChange,
}: {
  role: RoleListItem
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useAdminPermissions()
  const catalogue = useAdminPermissionCatalogue()
  const update = useUpdateRolePermissions(role.roleId)

  const [selected, setSelected] = useState<Set<PermissionCode>>(new Set())
  const [result, setResult] = useState<DualControlResult | null>(null)

  useEffect(() => {
    if (open) {
      setSelected(new Set(role.permissions))
      setResult(null)
      update.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role.permissions])

  const settled = result !== null

  const toggle = (code: PermissionCode) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(code)) {
        next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit permissions for {role.name}</DialogTitle>
          <DialogDescription>
            The whole set is replaced. Every member holding this role is affected the moment this
            takes effect — check who is assigned it before narrowing what it grants.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            <AdminDualControlOutcome
              result={result}
              awaitingDescription={`Changing ${role.name}'s permissions is waiting for a countersignature. Everyone holding this role keeps what it grants today.`}
              appliedDescription={`${role.name}'s permissions are updated, for everyone who holds it.`}
            />
          ) : (
            <>
              <AdminDualControlNotice
                action="Changing this role's permissions"
                approvePermission={ADMIN_PERMISSIONS.roleManageApprove}
                canCountersign={permissions.canCountersignRolePermissions}
              />

              {role.isSystemRole ? (
                <Badge tone="warning">System role — check what depends on it before narrowing it</Badge>
              ) : null}

              {catalogue.isPending ? (
                <Skeleton className="h-40 w-full" />
              ) : catalogue.isError ? (
                <ErrorSurface error={catalogue.error} onRetry={() => void catalogue.refetch()} />
              ) : (
                <div className="max-h-80 space-y-3 overflow-y-auto">
                  {(catalogue.data ?? []).map((group) => (
                    <div key={group.module}>
                      <p className="text-xs font-semibold text-heading">
                        {MODULE_LABELS[group.module] ?? group.module}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {group.permissions.map((permission) => (
                          <li key={permission.code}>
                            <label className="flex items-start gap-2 rounded-control p-1 text-sm hover:bg-surface-hover">
                              <Checkbox
                                checked={selected.has(permission.code)}
                                onCheckedChange={() => toggle(permission.code)}
                                className="mt-0.5"
                              />
                              <span className="min-w-0">
                                <span className="font-mono text-xs text-brand-text">{permission.code}</span>
                                <span className="block text-xs text-muted">{permission.description}</span>
                              </span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {update.isError ? <ProblemAlert error={update.error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={update.isPending}
                onClick={() => {
                  update.mutate({ permissionCodes: [...selected] }, { onSuccess: setResult })
                }}
              >
                {update.isPending ? 'Working…' : 'Submit the change'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
